@if (propertiesError()) {
  <div class="flex justify-center flex-1 p-4">
    <cmi-notification
      mode="error"
      [message]="'common.notifications.errorTitle' | translate"
      [description]="'common.notifications.loadingFailed' | translate" />
  </div>
} @else if (propertiesLoading()) {
  <div class="flex items-center justify-center flex-1">
    <cmi-loading-spinner />
  </div>
} @else {
  <div class="space-y-4">
    @if (saveError()) {
      <cmi-notification
        mode="error"
        [message]="'parameterSettings.saveError' | translate"
        [description]="saveError()!" />
    }

    <p class="font-medium text-gray-800">{{ parameterName() }}</p>

    <!-- Mode selector -->
    <div class="flex overflow-hidden rounded-lg border border-gray-200">
      <button
        type="button"
        (click)="mode.set('wizard')"
        [class]="
          mode() === 'wizard'
            ? 'flex-1 px-3 py-2 text-sm font-medium bg-main text-white'
            : 'flex-1 px-3 py-2 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50'
        ">
        {{ 'parameterSettings.modeWizard' | translate }}
      </button>
      <button
        type="button"
        (click)="mode.set('fixed')"
        [class]="
          mode() === 'fixed'
            ? 'flex-1 border-l border-gray-200 px-3 py-2 text-sm font-medium bg-main text-white'
            : 'flex-1 border-l border-gray-200 px-3 py-2 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50'
        ">
        {{ 'parameterSettings.modeFixed' | translate }}
      </button>
      @if (supportsSelector() || isSelectorReadOnly()) {
        <button
          type="button"
          (click)="mode.set('selector')"
          [class]="
            mode() === 'selector'
              ? 'flex-1 border-l border-gray-200 px-3 py-2 text-sm font-medium bg-main text-white'
              : 'flex-1 border-l border-gray-200 px-3 py-2 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50'
          ">
          {{ 'parameterSettings.modeSelector' | translate }}
        </button>
      }
    </div>

    @if (mode() === 'wizard') {
      <!-- UI Bezeichnung -->
      <div class="space-y-1">
        <label class="block text-sm font-medium text-gray-700">
          {{ 'parameterSettings.uiLabel' | translate }}
        </label>
        <input
          type="text"
          [value]="uiLabel()"
          (input)="onUiLabelInput($event)"
          [placeholder]="'parameterSettings.uiLabelPlaceholder' | translate"
          class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-main" />
      </div>

      <!-- Type-specific default value -->
      <div class="space-y-1">
        <label class="block text-sm font-medium text-gray-700">
          {{ 'parameterSettings.defaultValue' | translate }}
        </label>
        @if (parameterType() === 'text') {
          <input
            type="text"
            [value]="textDefault()"
            (input)="onTextDefaultInput($event)"
            [placeholder]="'parameterSettings.textDefaultPlaceholder' | translate"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-main" />
        } @else if (parameterType() === 'date') {
          <input
            type="text"
            [value]="dateDefault()"
            (input)="onDateDefaultInput($event)"
            [placeholder]="'parameterSettings.dateDefaultPlaceholder' | translate"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-main" />
        } @else if (parameterType() === 'number') {
          <input
            type="number"
            [value]="numberDefault()"
            (input)="onNumberDefaultInput($event)"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-main" />
        } @else if (parameterType() === 'boolean') {
          <div class="flex items-center gap-2">
            <input
              id="boolDefault"
              type="checkbox"
              [checked]="boolDefault()"
              (change)="onBoolDefaultChange($event)"
              class="size-4 rounded border-gray-300 accent-main" />
            <label for="boolDefault" class="text-sm text-gray-700">
              {{
                boolDefault()
                  ? ('parameterSettings.yes' | translate)
                  : ('parameterSettings.no' | translate)
              }}
            </label>
          </div>
        }
      </div>
    } @else if (mode() === 'fixed') {
      <!-- Fixed value -->
      <div class="space-y-1">
        <label class="block text-sm font-medium text-gray-700">
          {{ 'parameterSettings.fixedValue' | translate }}
        </label>
        @if (parameterType() === 'text') {
          <input
            type="text"
            [value]="fixedText()"
            (input)="onFixedTextInput($event)"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-main" />
        } @else if (parameterType() === 'date') {
          <input
            type="date"
            [value]="fixedDate()"
            (change)="onFixedDateChange($event)"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-main" />
        } @else if (parameterType() === 'number') {
          <input
            type="number"
            [value]="fixedNumber()"
            (input)="onFixedNumberInput($event)"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-main" />
        } @else if (parameterType() === 'boolean') {
          <div class="flex items-center gap-2">
            <input
              id="fixedBoolean"
              type="checkbox"
              [checked]="fixedBoolean()"
              (change)="onFixedBooleanChange($event)"
              class="size-4 rounded border-gray-300 accent-main" />
            <label for="fixedBoolean" class="text-sm text-gray-700">
              {{
                fixedBoolean()
                  ? ('parameterSettings.yes' | translate)
                  : ('parameterSettings.no' | translate)
              }}
            </label>
          </div>
        }
      </div>
    } @else if (mode() === 'selector') {
      <!-- Selector mode -->
      @if (isSelectorReadOnly()) {
        <div class="space-y-1">
          <label for="readonly-selector-provider" class="block text-sm font-medium text-gray-700">
            {{ 'parameterSettings.selectorProvider' | translate }}
          </label>
          <input
            id="readonly-selector-provider"
            type="text"
            [value]="selectedProvider()"
            readonly
            class="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500 cursor-not-allowed" />
        </div>
        <!-- Read-only: server does not support selectors -->
        <cmi-notification
          mode="info"
          [description]="'parameterSettings.selectorReadOnlyHint' | translate" />
      } @else {
        <div class="space-y-1">
          <label for="editor-selector-provider" class="block text-sm font-medium text-gray-700">
            {{ 'parameterSettings.selectorProvider' | translate }}
          </label>
          @if (selectorProvidersLoading()) {
            <div class="flex items-center justify-center py-2">
              <cmi-loading-spinner />
            </div>
          } @else if (selectorProvidersError()) {
            <cmi-notification
              mode="error"
              [message]="'common.notifications.errorTitle' | translate"
              [description]="'common.notifications.loadingFailed' | translate"
              [actionLabel]="'common.notifications.retry' | translate"
              (action)="reloadSelectorProviders()" />
          } @else {
            <div
              class="relative flex items-center rounded-md bg-white outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-main">
              <select
                id="editor-selector-provider"
                (change)="onProviderChange($event)"
                class="w-full appearance-none bg-transparent py-1.5 pl-3 pr-8 text-base text-gray-900 focus:outline-none sm:text-sm/6">
                <option value="" [selected]="selectedProvider() === ''">
                  {{ 'parameterSettings.selectorProviderPlaceholder' | translate }}
                </option>
                @for (provider of selectorProviders(); track provider.key) {
                  <option [value]="provider.key" [selected]="provider.key === selectedProvider()">
                    {{ provider.displayName }}
                  </option>
                }
              </select>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="pointer-events-none absolute right-2 size-4 text-gray-500">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </div>
          }
        </div>
      }
    }

    @if (!isSelectorReadOnly() || mode() !== 'selector') {
      <!-- Action buttons -->
      <div class="flex gap-2">
        <button
          type="button"
          (click)="save()"
          [disabled]="isSaving()"
          class="px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-main focus:ring-offset-2 flex items-center justify-center gap-2 border-main text-main border hover:text-white hover:bg-main/90">
          {{ (isSaving() ? 'parameterSettings.saving' : 'parameterSettings.save') | translate }}
        </button>
      </div>
    }
  </div>
}
